<script lang="ts" setup>
    import useAcfOptions from "../../composables/use-acf-options";
    import useMenu from "../../composables/use-menu";
    import { useRoute } from "vue-router";

    const props = defineProps<{
        page: any
    }>();

    const route = useRoute();
    const currentPage = route.path.toString();
    const menu: any = await useMenu('main-menu');

    const mainLogo: any = await useAcfOptions('main_logo');

    useHead({
        link: [
            {
                href: mainLogo.url,
                fetchpriority: 'high',
                rel: 'preload',
                as: 'image',
            }
        ],
    });

    let activeMenu = '';

    // Normalize current page path by removing trailing slash
    const normalizedCurrentPage = currentPage.replace(/\/$/, '') || '/';

    menu?.menuItems?.nodes.forEach((navMenu: any) => {
        // Normalize menu URI by removing trailing slash
        const normalizedMenuUri = navMenu?.uri?.replace(/\/$/, '') || '/';

        if (normalizedCurrentPage.startsWith(normalizedMenuUri) && normalizedMenuUri !== '/') {
            activeMenu = navMenu?.uri;
            return;
        } else if (navMenu?.childItems?.nodes?.length > 0) {
            navMenu?.childItems?.nodes.forEach((childMenu: any) => {
                // Normalize child menu URI by removing trailing slash
                const normalizedChildUri = childMenu?.uri?.replace(/\/$/, '') || '/';

                if (normalizedCurrentPage.startsWith(normalizedChildUri) && normalizedChildUri !== '/') {
                    activeMenu = navMenu?.uri;
                    return;
                }
            });
        }
    });

</script>
<template>
    <!-- Mobile Header Bar -->
    <nav id="mobile-navbar" class="navbar navbar-expand-md border-0 fixed-top pb-0 pt-0 d-block d-md-none">
        <div class="navbar-toggler py-2 d-flex" style="
            border: none;
            border-radius: 0;
            justify-content: space-between;
            width: 100%;
            padding: 0;
            background: white;">
            <div class="d-flex">
                <a href="/" aria-label="Marella Homes">
                    <NuxtImg id="mobile-header-logo"
                    src="/marella-mobile-header.svg" width="200" height="60"
                    alt="Marella Homes"
                    quality="80"/>
                </a>
            </div>
            <div class="d-flex pe-3">
                <button type="button" data-bs-target="#mobile-sidebar" data-bs-toggle="offcanvas" aria-controls="mobile-sidebar" aria-label="Toggle menu" class="navbar-toggler border-0 shadow-none px-0 me-2 mobile-toggle-btn">
                    <div class="hamburger-icon">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </button>
            </div>
        </div>
    </nav>

    <!-- Mobile Offcanvas Menu -->
    <div id="mobile-sidebar" aria-labelledby="mobile-sidebar-label" class="offcanvas offcanvas-end border-0 d-block d-md-none">
        <div class="offcanvas-body pt-0">
            <ul id="mobile-nav-ul" class="navbar-nav">
                <template v-for="(item, index) in menu?.menuItems?.nodes">
                    <li v-if="item?.childItems?.nodes.length > 0" :class="`mobile-nav-item has-dropdown ${activeMenu === item?.uri ? 'active' : ''}`">
                        <input type="checkbox" :id="`dropdown-${index}`" class="dropdown-toggle" hidden>
                        <label :for="`dropdown-${index}`" class="mobile-nav-link-wrapper">
                            <a :class="`mobile-nav-link text-uppercase`" :href="item?.uri">{{ item?.label }}</a>
                            <span class="mobile-dropdown-arrow">
                                <span class="arrow-down"></span>
                            </span>
                        </label>
                        <ul class="mobile-dropdown-menu">
                            <li v-for="child in item?.childItems?.nodes" :class="`mobile-dropdown-item ${currentPage.startsWith(child?.uri) ? 'active' : ''}`">
                                <a :href="child?.uri" class="mobile-dropdown-link">
                                    {{ child?.label }}
                                </a>
                            </li>
                        </ul>
                    </li>
                    <li v-else :class="`mobile-nav-item ${activeMenu === item?.uri ? 'active' : ''}`">
                        <div class="mobile-nav-link-wrapper">
                            <a :class="`mobile-nav-link text-uppercase`" :href="item?.uri">{{ item?.label }}</a>
                        </div>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<style scoped>
/* Mobile Navigation Styles */
#mobile-navbar {
    background: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    z-index: 1056; /* Higher than offcanvas default z-index */
}

#mobile-sidebar {
    background: white;
    border-radius: 0 0 0 50px;
    width: 90%;
    height: calc(100% - 100px); /* Account for fixed header height */
    top: 100px; /* Start below the fixed header */
    margin-top: 0;
}

/* Make sure offcanvas appears seamlessly connected to header */
#mobile-sidebar.show {
    box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
}

#mobile-sidebar .offcanvas-body {
    padding: 0;
}

/* Hamburger Menu Animation */
.hamburger-icon {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 34px;
    height: 28px;
    cursor: pointer;
    position: relative;
}

.hamburger-icon span {
    display: block;
    height: 1px;
    width: 100%;
    background-color: #091a2c;
    transition: all 0.3s ease;
    transform-origin: center;
}

.hamburger-icon span:nth-child(1) {
    margin-bottom: 6px;
}

.hamburger-icon span:nth-child(2) {
    margin-bottom: 6px;
}

/* Active state - transform to X like in the design */
.mobile-toggle-btn.is-active .hamburger-icon span:nth-child(1) {
    transform: translateY(14px) rotate(45deg);
}

.mobile-toggle-btn.is-active .hamburger-icon span:nth-child(2) {
    opacity: 0;
}

.mobile-toggle-btn.is-active .hamburger-icon span:nth-child(3) {
    transform: translateY(-14px) rotate(-45deg);
}

#mobile-nav-ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.mobile-nav-item {
    padding: 0;
}

.mobile-nav-item:last-child {
    padding: 0;
}

.mobile-nav-link-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem;
    width: 100%;
    cursor: pointer;
    border-bottom: 0.5px solid #000;
}

.dropdown-toggle {
    display: none;
}

.mobile-nav-link {
    color: var(--var-dark-color);
    text-decoration: none;
    font-size: 1.4rem;
    font-weight: 400;
    flex: 1;
}

.mobile-dropdown-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    transition: transform 0.3s ease;
    pointer-events: none;
    padding: 10px;
    position: relative;
}

.arrow-down {
    width: 15px;
    height: 15px;
    border-right: 1px solid #000;
    border-bottom: 1px solid #000;
    transform: rotate(45deg);
    transition: transform 0.3s ease;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -12.5px;
    margin-left: -7.5px;
}

.dropdown-toggle:checked + .mobile-nav-link-wrapper .mobile-dropdown-arrow {
    transform: rotate(180deg);
}

.mobile-dropdown-menu {
    list-style: none;
    padding: 0;
    margin: 0;
    background: transparent;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
}

.dropdown-toggle:checked ~ .mobile-dropdown-menu {
    max-height: 300px;
}

.mobile-dropdown-link {
    display: block;
    padding: 1rem 2rem;
    color: var(--var-dark-color);
    text-decoration: none;
    font-size: 1.1rem;
    font-weight: 300;
    text-transform: capitalize;
}

.mobile-dropdown-link:hover {
    color: var(--bs-primary);
    background: none;
}
</style>